import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { Box, Dialog, DialogContent, DialogTitle, ListItemIcon, TextField, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import LazyImage from '../components/LazyImage/index';
import { Button } from './Button';
import { toast } from 'react-toastify';
import { styled } from './styles/stitches.styles';

interface Props {
  isOpened: boolean;
  onClose: () => void;
  onSubmit: (newNickName: string) => Promise<void>;
  initialValues: { newNickName: string; oldNickName: string };
  isSuccess: boolean;
  linkToBot: string;
  className?: string;
}

const NAME_MAX_LENGTH = 10;
const NEWKICKNAME_MAX_LENGTH = 15;

const SuccessContainer = styled('div', {
  display: 'grid',
  gap: 16,
  alignItems: 'center'
});

const TextSmall = styled(Typography, {
  color: 'white',
  fontSize: '12px'
});

const SuccessSubmit = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16
});

let ChangeNickNameModal: FC<Props> = ({
  isOpened,
  onClose,
  onSubmit,
  initialValues,
  isSuccess,
  linkToBot,
  className
}) => {
  const t = useTranslations();
  const [newNickName, setNewNickName] = useState(initialValues.newNickName || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingNewNickName, setIsEditingNewNickName] = useState(!initialValues.newNickName);

  useEffect(() => {
    setNewNickName(initialValues.newNickName || '');
    setIsEditingNewNickName(!initialValues.newNickName);
  }, [initialValues.newNickName]);

  const prepareSubmit = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setIsEditingNewNickName(false);

    await onSubmit(newNickName);

    setIsLoading(false);
    setIsEditingNewNickName(true);
  };

  const handleClose = () => {
    setNewNickName('');

    onClose();
  };

  const pasteText = async (setNickName: (el: string) => void, setEdit: (el: boolean) => void) => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.length <= NAME_MAX_LENGTH) {
        setNickName(text);
        setEdit(true);
      } else {
        toast.error(t('common.nickname.err'));
      }
    } catch (err) {
      console.error('Ошибка при чтении из буфера обмена', err);
    }
  };

  const goToLink = () => {
    window.open(linkToBot, '_blank');
  };

  return (
    <Dialog
      className={className}
      aria-labelledby='entrance-dialog'
      open={isOpened}
      maxWidth='sm'
      fullWidth
      scroll='body'
      onClose={handleClose}
    >
      <DialogTitle sx={{ background: '#1C1929', position: 'relative' }}>
        <Typography
          variant='h6'
          sx={{
            color: 'white',
            marginBottom: { sm: 0 },
            textAlign: 'center',
            px: 1,
            whiteSpace: 'pre-wrap'
            // fontSize: isSuccess ? undefined : '16px',
            // marginTop: isSuccess ? 0 : '20px'
          }}
        >
          {t(isSuccess ? 'common.nickname.titleSuccess' : 'common.nickname.title')}
        </Typography>

        <Box sx={{ position: 'absolute', top: 20, right: 24, cursor: 'pointer' }} onClick={handleClose}>
          <LazyImage src={'/images/icons/landing/close.svg'} alt='close' width={24} height={24} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ background: '#1C1929' }}>
        {isSuccess ? (
          <SuccessContainer>
            <div className='change_name_wrapper_input'>
              <div className='change_name_wrapper_input_wrap'>
                <TextSmall>{t('common.nickname.successNew')}</TextSmall>
                <TextField
                  value={newNickName.startsWith('@') ? newNickName : `@${newNickName}`}
                  onChange={() => null}
                  placeholder={t('common.nickname.successNew')}
                  variant='filled'
                  disabled
                  style={{ color: '#FFF' }}
                />
              </div>
            </div>
            <SuccessSubmit>
              <Button
                sx={{
                  color: 'white',
                  cursor: 'pointer',
                  padding: '5px 15px',
                  borderRadius: 15,
                  background: '#ffffff1A'
                }}
                onClick={goToLink}
              >
                {t('common.nickname.go')}
              </Button>
              <Button
                sx={{
                  color: 'white',
                  cursor: 'pointer',
                  padding: '5px 15px',
                  borderRadius: 15,
                  background: '#ffffff1A'
                }}
                onClick={handleClose}
              >
                {t('common.nickname.cancel')}
              </Button>
            </SuccessSubmit>
          </SuccessContainer>
        ) : (
          <div className='change_name_wrapper'>
            <div className='change_name_wrapper_input'>
              <div className='change_name_wrapper_input_wrap'>
                <TextSmall>{t('common.nickname.old')}</TextSmall>
                <TextField
                  value={initialValues.oldNickName}
                  onChange={() => null}
                  variant='filled'
                  disabled
                  style={{ color: '#FFF' }}
                />
              </div>
            </div>
            <div className='change_name_wrapper_input_wrap'>
              <TextSmall>{t('common.nickname.new')}</TextSmall>
              <div className='change_name_wrapper_input'>
                <TextField
                  value={newNickName}
                  onChange={({ target }) =>
                    target.value.length > NEWKICKNAME_MAX_LENGTH ? null : setNewNickName(target.value)
                  }
                  placeholder={t('common.nickname.new')}
                  variant='filled'
                  disabled={!isEditingNewNickName}
                  style={{ color: '#FFF' }}
                />
                <ListItemIcon
                  sx={{ justifyContent: 'end', minWidth: 'min-content !important' }}
                  onClick={() => pasteText(setNewNickName, setIsEditingNewNickName)}
                >
                  <CopyIcon sx={{ width: 24, height: 24, color: 'white' }} fontSize='small' />
                </ListItemIcon>
              </div>
            </div>
            <div className='change_name_wrapper_submit'>
              <Button
                sx={{
                  color: 'white',
                  cursor: 'pointer',
                  padding: '5px 15px',
                  borderRadius: 15,
                  background: '#ffffff1A',
                  margin: '0 auto'
                }}
                onClick={prepareSubmit}
                disabled={isLoading}
              >
                {t('header.dropdown.save')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

ChangeNickNameModal = styled(ChangeNickNameModal, {
  '& .MuiPaper-root': {
    maxWidth: '500px !important'
  },

  '& .change_name_wrapper_input': {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',

    '& > .MuiFormControl-root': {
      width: '100%',

      '& .MuiFilledInput-input.Mui-disabled': {
        '-webkit-text-fill-color': '#FFF !important',
        opacity: 0.6
      }
    }
  },
  '& .change_name_wrapper_input_wrap': {
    width: '100%',

    '& > .MuiFormControl-root': {
      width: '100%',

      '& .MuiFilledInput-input.Mui-disabled': {
        '-webkit-text-fill-color': '#FFF !important',
        opacity: 0.6
      }
    }
  },
  '& .change_name_wrapper_submit': {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px'
  },
  '& .change_name_wrapper_edit': {
    backgroundColor: 'transparent',
    border: 'none'
  },
  '& .change_name_wrapper': {
    display: 'grid',
    gap: '16px',
    width: '100%',

    '&_input': {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      width: '100%',

      '& > .MuiFormControl-root': {
        width: '100%',

        '& .MuiFilledInput-input.Mui-disabled': {
          '-webkit-text-fill-color': 'unset !important',
          opacity: 0.6
        }
      }
    },
    '&_input_wrap': {
      width: '100%',
      display: 'grid',
      gap: '4px',

      '& .Mui-disabled': {
        '-webkit-text-fill-color': 'unset !important'
      }
    },

    '&_submit': {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '16px'
    },
    '&_edit': {
      backgroundColor: 'transparent',
      border: 'none'
    }
  }
});

export { ChangeNickNameModal };
